import React, { useState, useEffect, useContext } from "react";
import { useLocation, useHistory, withRouter } from "react-router-dom";
import { observer } from "mobx-react";
import {
  callsStore,
  defaultCustomDate,
  defaultFilterObject
} from "Stores/Calls";
import { toasterState } from "Components/common/toaster";
import userDetail from "Stores/userDetail";
import Utils from "Utils/utils";
import CallsUtils from "Utils/CallsUtils";
import { MP_EVENT } from "Static/constant";
import UserFilterDropDown from "./UserFilterDropDown";
import DateRangePicker from "Pages/Dashboard/reports/DateRangePicker";
import { CallsFilterContext } from "./CallsModule";

const formatDateTime = date => {
  const datePart = date.toLocaleString("sv-SE", {
    year: "numeric",
    month: "numeric",
    day: "numeric"
  });
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");
  const timezoneOffset = Utils.getTimezoneOffsetISO();
  return `${datePart}T${hours}:${minutes}:${seconds}${timezoneOffset}`;
};

// ?session=<id>&from=<ms>&to=<ms> from the power dialer's "View in Calls"
const getSessionFilter = search => {
  const params = new URLSearchParams(search);
  const from = +params.get("from");
  if (!params.get("session") || !from) return null;
  return { from, to: +params.get("to") || Date.now() };
};

const sessionLabel = ms => {
  const d = new Date(ms);
  const day =
    d.toDateString() === new Date().toDateString() ? "Today" : Utils.formatDate(d);
  return `${day} ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`;
};

const CallsFilter = observer(() => {
  const { teamLoader, isContactsAdded, inFreePlan, isOverview, pageType } =
    useContext(CallsFilterContext);

  let location = useLocation();
  const history = useHistory();
  const sessionFilter = getSessionFilter(location.search);
  const {
    team = false,
    hasTeamMember = false,
    memberId = "",
    isTeamOwner = false
  } = userDetail?.userInfo || {};

  const [data, setData] = useState(callsStore.filterValues);

  const dateObjFunction = {
    today: 0,
    week: 6, // Doesn't include current day of the week
    month: 29, // Doesn't include current day of the month
    yesterday: 1,
    customDate: "custom"
  };

  const getInitialDisplayUser = (selectedUser, teamMembers, isTeam, currentMemberId) => {
    if (!selectedUser || selectedUser === "all") return isTeam ? "all" : "you";
    if (Array.isArray(selectedUser) && selectedUser.length > 0) {
      const matched = (teamMembers || []).filter(m => selectedUser.includes(m.memberId));
      if (!matched.length) return isTeam ? "all" : "you";
      const names = matched.map(m => {
        const { firstName = "", lastName = "", email = "" } = m;
        return (firstName && lastName && firstName[0] + lastName[0]) ||
          (firstName?.length > 2 && firstName?.slice(0, 2)) ||
          (lastName?.length > 2 && lastName?.slice(0, 2)) ||
          email?.slice(0, 2);
      }).filter(Boolean);
      return names.length ? names : isTeam ? "all" : "you";
    }
    return isTeam ? "all" : "you";
  };

  const updateData = (obj = {}) => {
    let newObject = { ...data, ...obj };
    setData(newObject);
    callsStore.setFilterValues(newObject);
  };

  const getSelectedUsers = obj => {
    const memberIds =
      obj && typeof obj === "object" && Object.keys(obj)?.length > 0
        ? obj
        : obj?.length && obj?.toLowerCase() === "you"
        ? new Array(memberId)
        : "";
    return memberIds;
  };

  const makeFilterRequest = (payload = {}, user, obj = {}) => {
    const selectedIds = getSelectedUsers(user);
    if (selectedIds?.length > 0) {
      payload.memberIds = selectedIds;
    } else {
      payload.memberIds = (!team && [memberId]) || null;
    }
    callsStore.setFilterPayload(payload);
    filterByDateTime(payload);
  };

  const resetToDefaultPage = () => {
    callsStore.setFilterPayload({
      pageNo: 1
    });
  };

  const getTabValue = value => {
    if (!isOverview && data?.activeDateFilter !== value) {
      resetToDefaultPage();
    }
    let filterObj = {
      activeDateFilter: value
    };
    if (data.activeDateFilter === "customDate" && value !== "customDate") {
      filterObj = {
        ...filterObj,
        ...{
          customDateValue: "Custom",
          customDateFormat: "",
          customDate: defaultCustomDate,
          previousDateFilter: value
        }
      };
    }
    updateData(filterObj);
    const payload =
      (value !== "custom" &&
        CallsUtils.getFromToDateWithTimeZoneByDaysCount(
          dateObjFunction[value]
        )) ||
      "";
    payload.pageNo = 1;
    Utils.mixpanelTrack(MP_EVENT.CALLS_FILTERED_BY_USER, {
      filterType: value,
      pageType
    });
    makeFilterRequest(payload, data.selectedUser, {
      value,
      days: dateObjFunction[value]
    });
  };

  const getUserFilterPayload = () => {
    if (data.activeDateFilter === "customDate") {
      const { start: startDate, end: endDate } =
        callsStore?.filterValues?.customDateFormat || false;
      Utils.mixpanelTrack(MP_EVENT.CALLS_FILTERED_BY_USER, { pageType });
      return {
        fromDate: Utils.convertFromDateToLocalString(startDate),
        to: Utils.convertToDateToLocalString(endDate),
        timeZone: Utils.getCurrentUserTimeZone()
      };
    } else {
      return CallsUtils.getFromToDateWithTimeZoneByDaysCount(
        dateObjFunction[data.activeDateFilter]
      );
    }
  };

  const getSelectedUser = value => {
    !isOverview && resetToDefaultPage();
    updateData({
      selectedUser: value
    });
    makeFilterRequest(getUserFilterPayload(), value, false);
  };

  const constructCustomDatePayload = (start, end, timeData = null) => {
    if (!isOverview && Utils.getDateDiffNumber(start, end) > 91) {
      toasterState.setToastMsg(
        "The from and to dates cannot be more than 91 days apart",
        "info"
      );
      return;
    }
    !isOverview && resetToDefaultPage();
    // If time data is provided, use the combined datetime
    let fromDateString, toDateString;
    if (timeData && timeData.startDate && timeData.endDate) {
      // Format datetime with actual time from the Date object
      const startDate = new Date(timeData.startDate);
      const endDate = new Date(timeData.endDate);
      fromDateString = formatDateTime(startDate);
      toDateString = formatDateTime(endDate);
    } else {
      // Fallback to original behavior (date only)
      fromDateString = Utils.convertFromDateToLocalString(start);
      toDateString = Utils.convertToDateToLocalString(end);
    }

    const payload = {
      fromDate: fromDateString,
      to: toDateString,
      timeZone: Utils.getCurrentUserTimeZone(),
      pageNo: 1
    };

    if (timeData && timeData.startTime && timeData.endTime) {
      payload.startTime = timeData.startTime;
      payload.endTime = timeData.endTime;
    }

    Utils.mixpanelTrack(MP_EVENT.CALLS_FILTERED_BY_USER, {
      filterType: "Custom Date",
      pageType
    });
    makeFilterRequest(payload, data.selectedUser, {
      value: "customDate",
      days: Utils.getDateDiffNumber(start, end)
    });
    let dateValue =
      (start &&
        end &&
        `${Utils.formatDate(start)} -  ${Utils.formatDate(end)}`) ||
      "Custom";
    updateData({
      showDatePicker: false,
      customDateValue: dateValue,
      customDateFormat: { start, end, ...(timeData || {}) }
    });
  };

  const getCustomDatePayload = (timeData = null) => {
    let startDate, endDate;
    if (timeData && timeData.startDate && timeData.endDate) {
      startDate = timeData.startDate;
      endDate = timeData.endDate;
    } else {
      const dateSelection =
        data?.customDate?.[0] ||
        callsStore?.filterValues?.customDate?.[0] ||
        false;
      startDate = dateSelection?.startDate || "";
      endDate = dateSelection?.endDate || "";
    }
    if (startDate && endDate && data.activeDateFilter === "customDate") {
      constructCustomDatePayload(startDate, endDate, timeData);
    }
  };

  const openDatePicker = () => {
    if (isContactsAdded) {
      updateData({
        showDatePicker: !data.showDatePicker,
        previousDateFilter: data.activeDateFilter,
        activeDateFilter: "customDate"
      });
    }
  };

  const hideDatePicker = () => {
    const { start = new Date(), end = null } = data.customDateFormat || {};
    const customDate =
      data.customDateValue === "Custom"
        ? defaultCustomDate
        : [
            {
              endDate: end,
              startDate: start,
              key: "selection"
            }
          ];
    updateData({
      showDatePicker: !data.showDatePicker,
      activeDateFilter: data.previousDateFilter,
      customDate
    });
  };

  const getAnalyticsData = payload => {
    if (location?.pathname?.indexOf("analytics") !== -1) {
      callsStore.getTeamSummaryAnalyticsData(
        CallsUtils.getAnalyticsPayload(payload)
      );
      callsStore.getCallsAnalyticsData(CallsUtils.getAnalyticsPayload(payload));
    }
  };

  const getOverviewData = (payload = {}, isInit = false) => {
    if (location?.pathname?.indexOf("overview") !== -1) {
      const newObj = {
        ...payload,
        ...callsStore.filterPayload
      };
      callsStore.setResetCheckbox(true);
      callsStore.getCallsOutcomeFilterData(
        CallsUtils.getPayload(newObj, true),
        isInit
      );
      callsStore.getCallsOverviewData(CallsUtils.getPayload(newObj));
    }
  };

  const filterByDateTime = (data = {}, isInit = false) => {
    const payload = {
      ...data,
      pageNo: callsStore.filterPayload.pageNo || 1,
      limit: 10
    };
    if (location?.pathname?.indexOf("overview") !== -1) {
      getOverviewData(payload, isInit);
    } else if (location?.pathname?.indexOf("analytics") !== -1) {
      getAnalyticsData(payload);
    }
  };

  const getCustomDatePayloadObj = () => {
    const { startDate = "", endDate = "" } =
      callsStore?.filterValues?.customDate?.[0] || false;
    if (startDate && endDate) {
      if (!isOverview && Utils.getDateDiffNumber(startDate, endDate) > 91) {
        updateData({
          customDateValue: "Custom",
          activeDateFilter: "month",
          previousDateFilter: "yesterday",
          customDate: defaultCustomDate
        });
        return CallsUtils.getFromToDateWithTimeZoneByDaysCount(89); // Indicates month(30 days) but current reduce by one
      } else {
        return {
          fromDate: Utils.convertFromDateToLocalString(startDate),
          to: Utils.convertToDateToLocalString(endDate),
          timeZone: Utils.getCurrentUserTimeZone()
        };
      }
    } else {
      const { start = "", end = "" } =
        callsStore?.filterValues?.customDateFormat || false;
      if (start && end) {
        return {
          fromDate: Utils.convertFromDateToLocalString(start),
          to: Utils.convertToDateToLocalString(end),
          timeZone: Utils.getCurrentUserTimeZone()
        };
      }
    }
    return {};
  };

  const getInitialReportData = () => {
    const payload = constructFilterPayload();
    if (payload && Object.keys(payload)?.length) {
      filterByDateTime(payload, true);
      Utils.mixpanelTrack(MP_EVENT.CALLS_VIEWED, {
        pageType: isOverview ? "Overview Page" : "Analytics Page"
      });
    }
    callsStore.isLogUpdated && callsStore.setIsLogUpdated(false);
  };

  const constructFilterPayload = () => {
    if (sessionFilter) {
      const payload = {
        fromDate: formatDateTime(new Date(sessionFilter.from)).slice(0, 19),
        to: formatDateTime(new Date(sessionFilter.to)).slice(0, 19),
        timeZone: Utils.getCurrentUserTimeZone(),
        memberIds: [memberId],
        pageNo: 1
      };
      callsStore.setFilterPayload(payload);
      return payload;
    }
    const payload =
      data.activeDateFilter !== "customDate"
        ? CallsUtils.getFromToDateWithTimeZoneByDaysCount(
            dateObjFunction[data.activeDateFilter]
          )
        : getCustomDatePayloadObj();
    const selectedIds = getSelectedUsers(data.selectedUser);
    if (selectedIds?.length > 0) {
      payload.memberIds = selectedIds;
    } else {
      payload.memberIds = (!team && [memberId]) || null;
    }
    callsStore.setFilterPayload(payload);
    return payload || {};
  };

  const handleCustomDate = customDate => {
    updateData({ customDate });
  };

  useEffect(() => {
    callsStore.isLogUpdated && getInitialReportData();
  }, [callsStore.isLogUpdated]);

  useEffect(() => {
    return () => {
      updateData(defaultFilterObject);
    };
  }, []);

  useEffect(() => {
    getInitialReportData();
  }, [location.pathname, location.search]);

  if (sessionFilter) {
    return (
      <div className="callsFilter">
        <span className="sessionChip">
          <i className="material-icons">phone_forwarded</i>
          Power dialer session · {sessionLabel(sessionFilter.from)}
          <i
            className="material-icons close"
            onClick={() => history.replace(location.pathname)}
          >
            close
          </i>
        </span>
      </div>
    );
  }

  return (
    <div className="callsFilter">
      {((team && hasTeamMember?.length > 1) || isTeamOwner) && !teamLoader && (
        <UserFilterDropDown
          team={team}
          selectedUserCbk={getSelectedUser}
          initialSelectedUser={getInitialDisplayUser(
            callsStore.filterValues.selectedUser,
            hasTeamMember,
            team,
            memberId
          )}
        />
      )}
      <div className="dateFilter">
        {data?.dateFilterList?.length &&
          data.dateFilterList.map(item => {
            const { name = "", id = "", value = "" } = item || {};
            return (
              <div
                className={`tabItem ${
                  data.activeDateFilter === value ? "active" : ""
                }`}
                id={id}
                key={id}
                onClick={() =>
                  value === "customDate" ? openDatePicker() : getTabValue(value)
                }
              >
                {name === "Custom" && (
                  <span className="material-icons-outlined dateRange">
                    date_range
                  </span>
                )}
                <span className="filterText">
                  {name === "Custom" ? data?.customDateValue : name}
                </span>
              </div>
            );
          })}
      </div>
      {data?.showDatePicker &&
        (() => {
          // Use current data.customDate instead of store to get the latest selection
          const baseDateObj =
            data?.customDate?.[0] ||
            callsStore.filterValues.customDate?.[0] ||
            false;
          const customDateFormat =
            data?.customDateFormat ||
            callsStore.filterValues.customDateFormat ||
            {};
          const { startDate, endDate, startTime, endTime } = customDateFormat;

          const customDateObjWithTime =
            baseDateObj && (startDate || endDate)
              ? {
                  startDate: startDate || baseDateObj.startDate,
                  endDate: endDate || baseDateObj.endDate,
                  ...(startTime && { startTime }),
                  ...(endTime && { endTime })
                }
              : baseDateObj;

          return (
            <DateRangePicker
              className="dateRangePicker"
              customDate={data.customDate}
              filterCbk={getCustomDatePayload}
              cancelCbk={hideDatePicker}
              insightPage={!isOverview}
              inFreePlan={inFreePlan}
              pageType="calls"
              upgradePopupName="callsCustomDate"
              handleCustomDate={handleCustomDate}
              customDateObj={customDateObjWithTime}
              enableTimeRange={true}
            />
          );
        })()}
      {data?.showDatePicker && (
        <div className="overlayCustomDateFilter" onClick={hideDatePicker} />
      )}
    </div>
  );
});

export default withRouter(CallsFilter);
export { CallsFilter };
